/**
 * Shared RPC bridge creation for parent (add-in) side.
 *
 * This module provides the common infrastructure for creating an RPC bridge
 * from the parent (Office add-in) to the child (chat iframe). Both Word and
 * Outlook add-ins use this to handle the ping/pong/init/ready handshake.
 */

import { MessagePortTransport } from "./transport";
import { exposeRpc, type ExposeRpcOptions } from "./server";
import { createRpcClient, type RpcClientOptions } from "./client";
import type { RpcPongMessage, RpcReadyMessage, RpcContext, RpcClient } from "./types";
import { consoleDev } from "../console-dev";

/**
 * Configuration for creating a parent RPC bridge.
 */
export interface ParentRpcBridgeConfig<TApiHandler extends object> {
  /** The iframe element containing the child */
  iframe: HTMLIFrameElement;
  /** The app type for context */
  app: "word" | "outlook";
  /** The workspace ID for context */
  workspaceId: string;
  /** Optional feature flags sent to the child during handshake */
  features?: RpcContext["features"];
  /** Factory function to create the API handler to expose to the child */
  createApiHandler: () => TApiHandler;
  /** Optional RPC client options */
  clientOptions?: RpcClientOptions;
  /** Optional error handler for RPC calls */
  onRpcError?: ExposeRpcOptions["onError"];
}

/**
 * Result of creating a parent RPC bridge.
 */
export interface ParentRpcBridge<TChildApi> {
  /** RPC client to call the child's API */
  readonly rpc: RpcClient<TChildApi>;
  /** The underlying transport (for advanced use cases) */
  readonly transport: MessagePortTransport;
  /** Clean up the bridge and stop listening for messages */
  cleanup: () => void;
}

/**
 * Create an RPC bridge from parent (add-in) to child (iframe).
 *
 * Handles the full handshake:
 * 1. Listen for rpc:ping from child, respond with rpc:pong
 * 2. Listen for rpc:init from child with MessagePort
 * 3. Set up transport, expose API handler, create RPC client
 * 4. Send rpc:ready with context to child
 *
 * @example
 * ```typescript
 * const bridge = await createParentRpcBridge<MyApiHandler, ChildApi>({
 *   iframe,
 *   app: "outlook",
 *   workspaceId: "123",
 *   createApiHandler: () => new MyApiHandler(),
 * });
 *
 * // Call child's API
 * await bridge.rpc.someMethod({ ... });
 *
 * // Clean up
 * bridge.cleanup();
 * ```
 */
export async function createParentRpcBridge<TApiHandler extends object, TChildApi>(
  config: ParentRpcBridgeConfig<TApiHandler>,
): Promise<ParentRpcBridge<TChildApi>> {
  const {
    iframe,
    app,
    workspaceId,
    createApiHandler,
    clientOptions = { requestTimeoutMs: 30000 },
    onRpcError,
  } = config;

  // Build log prefix from app name (e.g., "outlook" -> "[RPC:OutlookAddIn]")
  const appName = app.charAt(0).toUpperCase() + app.slice(1);
  const logPrefix = `[RPC:${appName}AddIn]`;

  // Mutable state - updated when child (re)connects
  let transport: MessagePortTransport | null = null;
  let rpc: RpcClient<TChildApi> | null = null;

  // Promise to resolve when initial connection is established
  let connectionResolve: (() => void) | null = null;

  /**
   * Handle messages from child iframe.
   * Responds to rpc:ping with rpc:pong (lightweight readiness check).
   * Handles rpc:init for full connection setup.
   */
  function handleChildMessage(event: MessageEvent): void {
    // Only accept messages from our iframe
    if (event.source !== iframe.contentWindow) return;
    if (!event.data || typeof event.data !== "object") return;

    // Handle ping - lightweight check that parent is listening
    if (event.data.type === "rpc:ping") {
      consoleDev.log(`${logPrefix} Received rpc:ping, sending rpc:pong`);
      const pongMessage: RpcPongMessage = { type: "rpc:pong" };
      iframe.contentWindow?.postMessage(pongMessage, "*");
      return;
    }

    // Handle init - full connection setup
    if (event.data.type !== "rpc:init") return;

    const port = event.ports[0];
    if (!port) {
      consoleDev.error(`${logPrefix} rpc:init received without MessagePort`);
      return;
    }

    consoleDev.log(`${logPrefix} Received rpc:init from child`);

    // Cleanup old connection if exists (handles iframe refresh)
    if (transport) {
      consoleDev.log(`${logPrefix} Cleaning up old connection`);
      transport.close();
    }

    // Create new transport on the received port
    transport = new MessagePortTransport(port);

    // Expose our API to the child
    const apiHandler = createApiHandler();
    exposeRpc(transport, apiHandler, {
      onError: onRpcError ?? ((err, method) => {
        consoleDev.error(`${logPrefix} RPC error in ${method}:`, err);
      }),
    });

    // Create client to call child's API
    rpc = createRpcClient<TChildApi>(transport, clientOptions);

    // Send ready message with context
    const context: RpcContext = { app, workspaceId, features: config.features };
    const readyMessage: RpcReadyMessage = { type: "rpc:ready", context };
    transport.send(readyMessage);

    consoleDev.log(`${logPrefix} Sent rpc:ready with context to child`);

    // Resolve initial connection promise (if waiting)
    if (connectionResolve) {
      connectionResolve();
      connectionResolve = null;
    }
  }

  // Start listening for child messages before iframe loads
  window.addEventListener("message", handleChildMessage);
  consoleDev.log(`${logPrefix} Listening for child messages...`);

  // Wait for initial connection
  await new Promise<void>((resolve) => {
    connectionResolve = resolve;
  });

  consoleDev.log(`${logPrefix} Initial connection established`);

  return {
    get rpc() {
      if (!rpc) throw new Error("Not connected to child");
      return rpc;
    },
    get transport() {
      if (!transport) throw new Error("Not connected to child");
      return transport;
    },
    cleanup: () => {
      window.removeEventListener("message", handleChildMessage);
      transport?.close();
      consoleDev.log(`${logPrefix} Cleaned up`);
    },
  };
}
